import { Link, router } from "@inertiajs/react"
import { useEffect, useId, useRef, useState } from "react"
import Avatar from "@/components/Avatar"
import type { CurrentUser } from "@/types"

const ITEM = "block w-full rounded-control px-3 py-2 text-left hover:bg-gray-10 focus-visible:bg-gray-10 dark:hover:bg-gray-800 dark:focus-visible:bg-gray-800"

export default function UserMenu({ user }: { user: CurrentUser }) {
  const [open, setOpen] = useState(false)
  const panelId = useId()
  const container = useRef<HTMLDivElement>(null)
  const button = useRef<HTMLButtonElement>(null)
  const { gitlab } = user

  useEffect(() => router.on("navigate", () => setOpen(false)), [])

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: PointerEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false)
        button.current?.focus()
      }
    }
    document.addEventListener("pointerdown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("pointerdown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open])

  return (
    <div ref={container} className="relative">
      <button
        ref={button}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={gitlab ? `Account menu, ${gitlab.name}, @${gitlab.username} on GitLab` : `Account menu, ${user.email}`}
        title={gitlab ? `${gitlab.name} (@${gitlab.username})` : user.email}
        onClick={() => setOpen(!open)}
        className="panel flex max-w-60 items-center gap-2.5 rounded-full py-1 pr-2 pl-1 hover:bg-gray-50 aria-expanded:bg-gray-50 sm:pr-3 dark:hover:bg-gray-800 dark:aria-expanded:bg-gray-800"
      >
        <Avatar identity={gitlab} fallback={user.email} />
        <span aria-hidden="true" className="min-w-0 text-left max-sm:hidden">
          <span className="block truncate text-sm leading-tight font-semibold">{gitlab?.name ?? user.email}</span>
          {gitlab && <span className="block truncate text-xs leading-tight text-gray-600 dark:text-gray-300">@{gitlab.username} on GitLab</span>}
        </span>
        <svg
          aria-hidden="true"
          viewBox="0 0 16 16"
          className="size-4 shrink-0 text-gray-600 dark:text-gray-300"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M4 6l4 4 4-4" />
        </svg>
      </button>
      <div
        id={panelId}
        hidden={!open}
        className="surface absolute right-0 z-20 mt-2 w-72 rounded-surface p-2 shadow-[0_16px_40px_-12px_rgb(24_23_29/0.35)] ring-1 ring-gray-100 max-sm:fixed max-sm:inset-x-4 max-sm:top-16 max-sm:mt-0 max-sm:w-auto dark:ring-gray-800"
      >
        <ul>
          <li>
            <Link href="/users/edit" className={ITEM}>
              Settings
            </Link>
          </li>
          <li>
            <Link href="/statuses/history" className={ITEM}>
              History
            </Link>
          </li>
          <li>
            <Link href="/about" className={ITEM}>
              About
            </Link>
          </li>
          {gitlab && (
            <li>
              <a href={`https://gitlab.com/${gitlab.username}`} className={ITEM}>
                Your GitLab profile
              </a>
            </li>
          )}
          <li>
            <Link href="/users/sign_out" method="delete" as="button" className={`${ITEM} text-red-600 dark:text-red-300`}>
              Log out
            </Link>
          </li>
        </ul>
      </div>
    </div>
  )
}
